/**
 * 보호자 활동 로그의 "변경 내용" 셀 렌더링
 */
import type { GuardianActivityLog } from '@/services/api';
import { TICKET_PRODUCT_LABEL } from '@/services/api';

function won(n: number) {
  return `${n.toLocaleString()}원`;
}

/**
 * BE 가 ISO 문자열(`2026-05-29T16:18:54.597111300`)을 그대로 넘겨주는데,
 * 화면에는 분 단위까지만 보여주는 게 깔끔.
 */
function fmtDateTime(s: string) {
  if (!s) return '-';
  // 마이크로초/타임존 제거 → "YYYY-MM-DD HH:mm"
  const cleaned = s.split('.')[0].replace('T', ' ');
  return cleaned.length >= 16 ? cleaned.slice(0, 16) : cleaned;
}

/**
 * productType → "(수업)" 같은 괄호 포함 라벨 반환.
 * 매핑 없거나 빈 값이면 빈 문자열 — "()" 가 화면에 노출되지 않음.
 */
function productLabelParen(
  k: keyof typeof TICKET_PRODUCT_LABEL | string | null | undefined,
): string {
  if (!k) return '';
  const label = TICKET_PRODUCT_LABEL[k as keyof typeof TICKET_PRODUCT_LABEL];
  return label ? `(${label})` : '';
}

export function formatGuardianLog(log: GuardianActivityLog): JSX.Element {
  const productLabel = productLabelParen;

  switch (log.eventCode) {
    case 'TICKET_PURCHASE': {
      const p = log.payload;
      const qty = p.purchaseQty ?? 1;
      const qtyLabel = qty > 1 ? `${qty}장 × ` : '';
      return (
        <span>
          이용권 구매 - {p.petName}, <strong>[{p.ticketName}]</strong>
          {productLabel(p.productType)},{qtyLabel}1장당 {p.quantity}회, {won(p.price)}
        </span>
      );
    }
    case 'TICKET_GRANTED': {
      const p = log.payload;
      const qty = p.purchaseQty ?? 1;
      const qtyLabel = qty > 1 ? `${qty}장 × ` : '';
      return (
        <span>
          이용권 발급 - {p.petName}, <strong>[{p.ticketName}]</strong>
          {productLabel(p.productType)},발급 사업자 {p.companyName},
          {' '}{qtyLabel}1장당 {p.quantity}회, {won(p.price)}
        </span>
      );
    }
    case 'TICKET_USE': {
      const p = log.payload;
      return (
        <span>
          이용권 사용 - 예약 {fmtDateTime(p.dtReservation)}, {p.petName},
          예약요청일 {fmtDateTime(p.dtRequested)},
          {productLabel(p.productType)},{p.companyName}
        </span>
      );
    }
    case 'TICKET_REVOKE': {
      const p = log.payload;
      return (
        <span>
          이용권 회수 - 취소 {fmtDateTime(p.dtCanceled)}, {p.petName},
          {productLabel(p.productType)},{p.companyName}
        </span>
      );
    }
    case 'NOTICE_RECEIVE': {
      const p = log.payload;
      return (
        <span>
          알림장 수신 - 발신 사업자 {p.fromCompanyName}, 수신 반려견 {p.petName}
        </span>
      );
    }
    case 'NOTICE_SEND': {
      const p = log.payload;
      return (
        <span>
          알림장 발신 - 수신 사업자 {p.toCompanyName}, 발신 반려견 {p.petName}
        </span>
      );
    }
  }
}
